// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Grid, Card, Container, Typography, Stack } from '@mui/material';
//
import { varFadeInUp, MotionInView } from '../../animate';
import { SxProps } from '@mui/system';
import { MotionViewFadeUp } from '../../MotionViewFadeUp';

// ----------------------------------------------------------------------

const shadowIcon = (color: string) => `drop-shadow(2px 2px 2px ${alpha(color, 0.48)})`;

const RootStyle = styled('div')();

const CardStyle = styled(Card)(({ theme }) => {
  return {
    maxWidth: 400,
    minHeight: 250,
    margin: 'auto',
    textAlign: 'center',
    padding: theme.spacing(5, 5),
    boxShadow: `0px 8px 16px rgba(145, 158, 171, 0.24)`,
    [theme.breakpoints.up('md')]: {
      backgroundColor:
        theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.common.black
    },
    '&:hover': {
      boxShadow: `0px 8px 16px rgba(145, 158, 171, 0.5)`
    }
  };
});

const CardIconStyle = styled('img')(({ theme }) => ({
  width: 80,
  height: 80,
  margin: 'auto',
  filter: shadowIcon(theme.palette.primary.main)
}));

// ----------------------------------------------------------------------

export interface LandingCardsProps {
  icon?: string;
  title: string;
  description: string;
}

export interface LandingMinimalHelpsProps {
  cards?: Array<LandingCardsProps>;
  title?: string;
  subtitle?: string;
  subtitleBold?: string;
  secondSubtitleBold?: string;
  sx?: SxProps;
  id?: string;
  withGradient?: boolean;
}

export default function LandingMinimalHelps({
  cards,
  title,
  subtitle,
  subtitleBold,
  secondSubtitleBold,
  sx,
  id,
  withGradient = true
}: LandingMinimalHelpsProps) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  return (
    <RootStyle
      sx={{
        ...sx,
        background: withGradient
          ? 'linear-gradient(360deg, rgba(223, 227, 232, 0.0001) 53.57%, #DFE3E8 116.08%)'
          : 'none'
      }}
      id={id}
    >
      <Container maxWidth="lg">
        <Container maxWidth="lg">
          <Stack spacing={5}>
            <MotionViewFadeUp>
              {title && (
                <>
                  <Typography
                    sx={{
                      marginTop: '1em',
                      textAlign: 'center',
                      display: {
                        xl: 'block',
                        lg: 'block',
                        md: 'block',
                        sm: 'none',
                        xs: 'none'
                      }
                    }}
                    variant="h2"
                  >
                    {title}
                  </Typography>
                  <Typography
                    sx={{
                      marginTop: '1em',
                      textAlign: 'center',
                      display: {
                        xl: 'none',
                        lg: 'none',
                        md: 'none',
                        sm: 'block',
                        xs: 'block'
                      }
                    }}
                    variant="h4"
                  >
                    {title}
                  </Typography>
                </>
              )}
            </MotionViewFadeUp>
            <MotionViewFadeUp>
              {subtitle && (
                <Typography
                  sx={{
                    color: 'text.secondary',
                    textAlign: 'center'
                  }}
                >
                  {subtitleBold && <Typography fontWeight={'bold'}>{subtitleBold}</Typography>}{' '}
                  {subtitle}
                  {secondSubtitleBold && (
                    <Typography fontWeight={'bold'}>{secondSubtitleBold}</Typography>
                  )}
                </Typography>
              )}
            </MotionViewFadeUp>
          </Stack>
        </Container>
        {cards && (
          <Grid mt={'3em'} container spacing={5} justifyContent="center">
            {cards.map((card: LandingCardsProps) => (
              <Grid key={card.title} item xs={12} md={4}>
                <MotionInView variants={varFadeInUp}>
                  <CardStyle>
                    {card.icon && <CardIconStyle src={card.icon} alt={card.title} />}
                    <Typography
                      variant="h5"
                      paragraph
                      sx={{
                        color: theme.palette.primary.main,
                        marginTop: card.icon ? '0em' : '2em',
                        padding: '15px 0 '
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: isLight ? 'text.secondary' : 'common.white' }}
                    >
                      {card.description}
                    </Typography>
                  </CardStyle>
                </MotionInView>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </RootStyle>
  );
}
